/**
 * Turns a dropped folder (or a set of picked files) into a stored corpus.
 *
 * Two entry points exist because the browser exposes folders two different
 * ways, and neither covers both cases:
 *   - drag-and-drop gives a DataTransfer with webkitGetAsEntry(), which is
 *     the ONLY way to walk a dropped directory tree.
 *   - the file picker with `webkitdirectory` gives a flat FileList whose
 *     members carry webkitRelativePath.
 * Both funnel into ingestFiles once paths are known.
 */

import { ulid } from "ulid"
import * as db from "./db"
import { extractFile, isIgnored, kindOf } from "./extract"

export interface IngestProgress {
  /** Files examined so far, out of the total discovered. */
  done: number
  total: number
  /** What is being parsed right now, for a progress label. */
  current: string
}

export interface IngestResult {
  corpusId: string
  name: string
  stored: number
  skipped: Array<{ path: string; reason: string }>
}

/** A file plus the path it should be stored under. */
export interface NamedFile {
  file: File
  path: string
}

/**
 * Walks a dropped DataTransfer into a flat list of files with relative paths.
 * Directory entries are recursed; anything else is taken as a single file.
 */
export async function filesFromDataTransfer(dt: DataTransfer): Promise<NamedFile[]> {
  const roots: FileSystemEntry[] = []
  // items must be read synchronously — the DataTransfer is neutered after
  // the event handler yields, so collect entries before any await.
  for (const item of Array.from(dt.items)) {
    const entry = item.webkitGetAsEntry?.()
    if (entry) roots.push(entry)
  }
  if (!roots.length) {
    return Array.from(dt.files).map(file => ({ file, path: file.name }))
  }

  const out: NamedFile[] = []
  await Promise.all(roots.map(entry => walkEntry(entry, "", out)))
  return out
}

async function walkEntry(entry: FileSystemEntry, prefix: string, out: NamedFile[]): Promise<void> {
  const path = prefix ? `${prefix}/${entry.name}` : entry.name
  if (isIgnored(path)) return

  if (entry.isFile) {
    const file = await new Promise<File | null>(resolve =>
      (entry as FileSystemFileEntry).file(resolve, () => resolve(null)))
    if (file) out.push({ file, path })
    return
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader()
    // readEntries returns at most ~100 per call and must be drained in a loop.
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>(resolve =>
        reader.readEntries(resolve, () => resolve([])))
      if (!batch.length) break
      await Promise.all(batch.map(e => walkEntry(e, path, out)))
    }
  }
}

/** Files from an <input webkitdirectory> or a plain multi-file picker. */
export function filesFromInput(list: FileList): NamedFile[] {
  return Array.from(list).map(file => ({
    // webkitRelativePath preserves the folder structure; a plain multi-select
    // has none, so the bare filename is the path.
    path: (file as any).webkitRelativePath || file.name,
    file,
  }))
}

/**
 * Extracts and stores every readable file. Unreadable ones are recorded as
 * skips rather than dropped silently — a corpus that quietly lost half its
 * documents is worse than one that says what it could not read.
 */
export async function ingestFiles(
  files: NamedFile[],
  opts: { name: string; teamId: string; onProgress?: (p: IngestProgress) => void },
): Promise<IngestResult> {
  const corpusId = ulid()
  const stored: db.CorpusFile[] = []
  const skipped: Array<{ path: string; reason: string }> = []

  // Strip the common leading folder so paths read "policies/intake.md"
  // rather than "MyDroppedFolder/policies/intake.md".
  const trimmed = stripCommonRoot(files)

  let done = 0
  for (const { file, path } of trimmed) {
    opts.onProgress?.({ done, total: trimmed.length, current: path })
    done++

    // Cheap rejects first — no point reading bytes off disk for a .png.
    if (isIgnored(path)) continue
    if (!kindOf(path)) { skipped.push({ path, reason: "unsupported file type" }); continue }

    const result = await extractFile(file, path)
    if (!result.ok || !result.text) {
      skipped.push({ path, reason: result.reason ?? "could not extract" })
      continue
    }
    stored.push({
      key: `${corpusId}:${path}`,
      corpusId,
      path,
      text: result.text,
      bytes: result.text.length,
      kind: result.kind!,
    })
  }
  opts.onProgress?.({ done: trimmed.length, total: trimmed.length, current: "" })

  if (stored.length) await db.putFiles(stored)

  const corpus: db.Corpus = {
    id: corpusId,
    name: opts.name,
    teamId: opts.teamId,
    fileCount: stored.length,
    totalBytes: stored.reduce((n, f) => n + f.bytes, 0),
    createdAt: Date.now(),
    skipped,
  }
  await db.putCorpus(corpus)

  return { corpusId, name: corpus.name, stored: stored.length, skipped }
}

/**
 * Drops the shared leading directory when every file has one — dropping a
 * folder otherwise buries every path under its name for no benefit.
 */
function stripCommonRoot(files: NamedFile[]): NamedFile[] {
  if (files.length === 0) return files
  const firstSeg = (p: string) => (p.includes("/") ? p.slice(0, p.indexOf("/")) : null)
  const root = firstSeg(files[0].path)
  if (!root) return files
  if (!files.every(f => firstSeg(f.path) === root)) return files
  return files.map(f => ({ ...f, path: f.path.slice(root.length + 1) }))
}

/** Name a corpus after the dropped folder when there is an obvious one. */
export function suggestName(files: NamedFile[]): string {
  if (!files.length) return "Documents"
  const first = files[0].path
  if (first.includes("/")) return first.slice(0, first.indexOf("/"))
  return files.length === 1 ? first : "Documents"
}
